import { useEffect, useMemo, useState } from 'preact/hooks';
import { api, mensagemDoErro, qs } from '../api';
import { decimal, inteiro, pct, plural } from '../format';
import { useApi } from '../hooks';
import { useMunicipios, useSecretarias } from '../lookups';
import { pode } from '../permissoes';
import { irPara } from '../router';
import { camada, pesquisaId } from '../state';
import { usuario } from '../state';
import type { PesquisaDetalhe, ProgressoColeta, ResultadoPesquisa } from '../types';
import { Barra, Campo, Carregando, ErroForm, Modal, Selo, avisar, confirmar } from '../ui';
import { dataHora } from './comum';
import { ConstrutorPerguntas, normalizarPerguntas } from './ConstrutorPerguntas';
import { SeletorLista } from './Seletores';
import { CampoData } from '../componentes/Campos';

export type Aba = 'dados' | 'perguntas' | 'metas' | 'coleta' | 'respostas' | 'resultado';
const ROTULO_ABA: Record<Aba, string> = { dados: 'Dados', perguntas: 'Perguntas', metas: 'Metas', coleta: 'Coleta', respostas: 'Respostas', resultado: 'Resultado' };
const ROTULO_STATUS = { RASCUNHO: 'Rascunho', ABERTA: 'Em coleta', ENCERRADA: 'Encerrada' } as const;
const TOM_STATUS = { RASCUNHO: 'cinza', ABERTA: 'verde', ENCERRADA: 'azul' } as const;

type Props = { id: string; abaInicial: Aba; onFechar: () => void; onMudou: () => void };

export function PesquisaModal({ id, abaInicial, onFechar, onMudou }: Props) {
  const podeEscrever = pode(usuario.value?.perfil, 'pesquisa:write');
  const [p, setP] = useState<PesquisaDetalhe | null>(null);
  const [erro, setErro] = useState('');
  const [aba, setAba] = useState<Aba>(abaInicial);

  async function carregar() {
    try {
      setP(await api<PesquisaDetalhe>(`/pesquisas/${id}`));
    } catch (e) {
      setErro(mensagemDoErro(e));
    }
  }
  useEffect(() => {
    void carregar();
  }, [id]);

  const mudou = async () => {
    await carregar();
    onMudou();
  };

  const abas: Aba[] = podeEscrever ? ['dados', 'perguntas', 'metas', 'coleta', 'respostas', 'resultado'] : ['resultado', 'dados'];

  return (
    <Modal titulo={p?.titulo ?? 'Pesquisa'} onFechar={onFechar} largo>
      {erro && <ErroForm texto={erro} />}
      {!p && !erro && <Carregando />}
      {p && (
        <>
          <div class="chips">
            <Selo texto={ROTULO_STATUS[p.status]} tom={TOM_STATUS[p.status]} />
            <span class="dica">{plural(p.respostas, 'resposta', 'respostas')} · {plural(p.municipiosComMeta, 'município', 'municípios')} com meta</span>
          </div>
          <div class="abas-modal" role="tablist" aria-label="Seções da pesquisa">
            {abas.map((a) => (
              <button key={a} type="button" role="tab" id={`aba-${a}`} aria-selected={aba === a} aria-controls={`painel-${a}`} class={aba === a ? 'aba ativa' : 'aba'} onClick={() => setAba(a)}>
                {ROTULO_ABA[a]}
              </button>
            ))}
          </div>
          <div role="tabpanel" id={`painel-${aba}`} aria-labelledby={`aba-${aba}`}>
            {aba === 'dados' && <Dados p={p} editavel={podeEscrever} onSalvo={mudou} />}
            {aba === 'perguntas' && <Perguntas p={p} onSalvo={mudou} />}
            {aba === 'metas' && <Metas p={p} onSalvo={mudou} />}
            {aba === 'coleta' && <Coleta p={p} />}
            {aba === 'respostas' && <Respostas p={p} onMudou={mudou} />}
            {aba === 'resultado' && <Resultado p={p} onFechar={onFechar} />}
          </div>
        </>
      )}
    </Modal>
  );
}

// ------------------------------------------------------------------ dados e ciclo de vida

function Dados({ p, editavel, onSalvo }: { p: PesquisaDetalhe; editavel: boolean; onSalvo: () => Promise<void> }) {
  const { lista: secretarias } = useSecretarias();
  const rascunho = p.status === 'RASCUNHO';
  const aberta = p.status === 'ABERTA';
  const [titulo, setTitulo] = useState(p.titulo);
  const [metodologia, setMetodologia] = useState(p.metodologia ?? '');
  const [ancora, setAncora] = useState(p.perguntaAncora);
  const [ini, setIni] = useState(p.periodoInicio);
  const [fim, setFim] = useState(p.periodoFim);
  const [secretariaId, setSecretariaId] = useState(p.secretariaId ?? '');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function salvar(e: Event) {
    e.preventDefault();
    setErro('');
    setSalvando(true);
    try {
      const body = rascunho
        ? { titulo: titulo.trim(), metodologia: metodologia.trim() || null, perguntaAncora: ancora.trim(), periodoInicio: ini, periodoFim: fim, secretariaId: secretariaId || null }
        : { titulo: titulo.trim(), metodologia: metodologia.trim() || null, periodoFim: fim };
      await api(`/pesquisas/${p.id}`, { method: 'PATCH', body });
      avisar('Dados salvos.');
      await onSalvo();
    } catch (err) {
      setErro(mensagemDoErro(err));
    } finally {
      setSalvando(false);
    }
  }

  async function abrir() {
    if (!(await confirmar('Abrir a pesquisa para coleta? Depois de aberta, as perguntas e o público não podem mais mudar.', { confirmar: 'Abrir para coleta' }))) return;
    try {
      await api(`/pesquisas/${p.id}/abrir`, { method: 'POST' });
      avisar('Pesquisa aberta: os pesquisadores já podem coletar.');
      await onSalvo();
    } catch (err) {
      setErro(mensagemDoErro(err));
    }
  }

  async function encerrar() {
    if (!(await confirmar('Encerrar a coleta? Não será possível lançar novas respostas nem reabrir. Os resultados continuam disponíveis.', { confirmar: 'Encerrar coleta', perigo: true }))) return;
    try {
      await api(`/pesquisas/${p.id}/encerrar`, { method: 'POST' });
      avisar('Coleta encerrada.');
      await onSalvo();
    } catch (err) {
      setErro(mensagemDoErro(err));
    }
  }

  const bloqueado = !editavel || p.status === 'ENCERRADA';
  return (
    <form class="form" onSubmit={salvar}>
      {aberta && editavel && <p class="aviso">Em coleta: só o título, a metodologia e a data final podem mudar.</p>}
      <Campo rotulo="Título">{(i) => <input id={i} required minLength={3} maxLength={200} disabled={bloqueado} value={titulo} onInput={(e) => setTitulo(e.currentTarget.value)} />}</Campo>
      <Campo rotulo="Sobre o quê">
        {(i) => (
          <select id={i} disabled={bloqueado || !rascunho} value={secretariaId} onChange={(e) => setSecretariaId(e.currentTarget.value)}>
            <option value="">Governo em geral</option>
            {secretarias.map((s) => <option key={s.id} value={s.id}>{s.sigla} — {s.nome}</option>)}
          </select>
        )}
      </Campo>
      <div class="linha-form">
        <Campo rotulo="Início da coleta" larg="meio">{(i) => <CampoData id={i} required disabled={bloqueado || !rascunho} value={ini} onChange={setIni} />}</Campo>
        <Campo rotulo="Fim da coleta" larg="meio">{(i) => <CampoData id={i} required disabled={bloqueado} value={fim} onChange={setFim} />}</Campo>
      </div>
      <Campo rotulo="Pergunta fixa (nota de 0 a 10)">{(i) => <input id={i} required minLength={5} maxLength={300} disabled={bloqueado || !rascunho} value={ancora} onInput={(e) => setAncora(e.currentTarget.value)} />}</Campo>
      <Campo rotulo="Metodologia" dica="Como as pessoas são escolhidas. A margem de erro só é confiável com amostra aleatória.">
        {(i) => <textarea id={i} rows={3} maxLength={2000} disabled={bloqueado} value={metodologia} onInput={(e) => setMetodologia(e.currentTarget.value)} />}
      </Campo>
      <ErroForm texto={erro} />
      {editavel && (
        <div class="modal-acoes">
          {rascunho && <button type="button" class="botao" onClick={() => void abrir()}>Abrir para coleta…</button>}
          {aberta && <button type="button" class="botao perigo" onClick={() => void encerrar()}>Encerrar coleta…</button>}
          {!bloqueado && <button type="submit" class="botao primario" disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar dados'}</button>}
        </div>
      )}
    </form>
  );
}

// ------------------------------------------------------------------ perguntas

function Perguntas({ p, onSalvo }: { p: PesquisaDetalhe; onSalvo: () => Promise<void> }) {
  const [perguntas, setPerguntas] = useState(p.questionario);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const rascunho = p.status === 'RASCUNHO';

  async function salvar() {
    setErro('');
    const limpas = normalizarPerguntas(perguntas);
    const i = limpas.findIndex((q) => q.texto.length < 3);
    if (i >= 0) return setErro(`A pergunta ${i + 1} precisa de um texto (mínimo 3 caracteres).`);
    const j = limpas.findIndex((q) => q.tipo === 'escolha' && (q.opcoes?.length ?? 0) < 2);
    if (j >= 0) return setErro(`A pergunta ${j + 1} precisa de ao menos 2 opções.`);
    setSalvando(true);
    try {
      await api(`/pesquisas/${p.id}`, { method: 'PATCH', body: { questionario: limpas } });
      avisar('Perguntas salvas.');
      await onSalvo();
    } catch (err) {
      setErro(mensagemDoErro(err));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div class="form">
      {!rascunho && <p class="aviso">As perguntas ficam congeladas depois que a pesquisa abre, para que todas as respostas sejam comparáveis.</p>}
      <ConstrutorPerguntas perguntas={perguntas} onMudar={setPerguntas} somenteLeitura={!rascunho} perguntaAncora={p.perguntaAncora} />
      <ErroForm texto={erro} />
      {rascunho && <div class="modal-acoes"><button type="button" class="botao primario" disabled={salvando} onClick={() => void salvar()}>{salvando ? 'Salvando…' : 'Salvar perguntas'}</button></div>}
    </div>
  );
}

// ------------------------------------------------------------------ metas

function Metas({ p, onSalvo }: { p: PesquisaDetalhe; onSalvo: () => Promise<void> }) {
  const { lista: municipios, nome } = useMunicipios();
  const [metas, setMetas] = useState<Record<string, number>>(() => Object.fromEntries((p.metas ?? []).map((m) => [m.codIbge, m.metaAmostra])));
  const [escolhidos, setEscolhidos] = useState<string[]>([]);
  const [padrao, setPadrao] = useState('30');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const travado = p.status === 'ENCERRADA';

  const cods = Object.keys(metas).sort((a, b) => nome(a).localeCompare(nome(b), 'pt-BR'));
  const total = cods.reduce((s, c) => s + (metas[c] ?? 0), 0);
  const livres = useMemo(() => municipios.filter((m) => !(m.cod in metas)).map((m) => ({ id: m.cod, rotulo: m.nome, sub: m.mesorregiao.replace(' Paraibano', '').replace(' Paraibana', '') })), [municipios, metas]);

  function adicionar() {
    const n = Number(padrao);
    if (!Number.isInteger(n) || n < 1) return setErro('A meta padrão precisa ser um número inteiro maior que zero.');
    setErro('');
    setMetas((m) => ({ ...m, ...Object.fromEntries(escolhidos.map((c) => [c, n])) }));
    setEscolhidos([]);
  }

  async function salvar() {
    setErro('');
    if (cods.length === 0) return setErro('Defina a meta de ao menos um município.');
    if (cods.some((c) => !Number.isInteger(metas[c]) || (metas[c] ?? 0) < 1)) return setErro('Toda meta precisa ser um número inteiro maior que zero.');
    setSalvando(true);
    try {
      await api(`/pesquisas/${p.id}/metas`, { method: 'PUT', body: { metas: cods.map((c) => ({ codIbge: c, metaAmostra: metas[c] })) } });
      avisar('Metas salvas.');
      await onSalvo();
    } catch (err) {
      setErro(mensagemDoErro(err));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div class="form">
      <p class="dica">Quantas entrevistas válidas você quer em cada município. Municípios com poucas respostas não têm resultado divulgado (mínimo de amostra), então prefira metas realistas.</p>
      {!travado && (
        <section class="bloco">
          <h3>Adicionar municípios</h3>
          <SeletorLista titulo="Municípios" itens={livres} marcados={escolhidos} onMudar={setEscolhidos} buscavel alturaMax={160} />
          <div class="linha-form alinhar">
            <label class="campo"><span>Meta de cada um</span><input type="number" min={1} step={1} value={padrao} onInput={(e) => setPadrao(e.currentTarget.value)} /></label>
            <button type="button" class="botao" disabled={escolhidos.length === 0} onClick={adicionar}>Adicionar {escolhidos.length > 0 ? plural(escolhidos.length, 'município', 'municípios') : ''}</button>
          </div>
        </section>
      )}

      <section class="bloco">
        <h3>{plural(cods.length, 'município', 'municípios')} · amostra prevista {inteiro(total)}</h3>
        {cods.length === 0 && <p class="vazio">Nenhuma meta definida ainda.</p>}
        <div class="tabela-rolagem pequena">
          <table class="tabela">
            <thead><tr><th scope="col">Município</th><th scope="col" class="num">Meta</th><th scope="col"><span class="so-leitor">Remover</span></th></tr></thead>
            <tbody>
              {cods.map((c) => (
                <tr key={c}>
                  <td>{nome(c)}</td>
                  <td class="num"><input type="number" min={1} step={1} value={metas[c]} disabled={travado} aria-label={`Meta de ${nome(c)}`} class="num-input"
                    onInput={(e) => setMetas((m) => ({ ...m, [c]: Number(e.currentTarget.value) }))} /></td>
                  <td>{!travado && <button type="button" class="link perigo-txt" aria-label={`Remover ${nome(c)}`} onClick={() => setMetas((m) => { const { [c]: _, ...resto } = m; return resto; })}>✕</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <ErroForm texto={erro} />
      {!travado && <div class="modal-acoes"><button type="button" class="botao primario" disabled={salvando} onClick={() => void salvar()}>{salvando ? 'Salvando…' : 'Salvar metas'}</button></div>}
    </div>
  );
}

// ------------------------------------------------------------------ coleta

function Coleta({ p }: { p: PesquisaDetalhe }) {
  const c = useApi<ProgressoColeta>(`/pesquisas/${p.id}/progresso`);
  if (c.carregando && !c.dados) return <Carregando />;
  if (c.erro) return <ErroForm texto={mensagemDoErro(c.erro)} />;
  const d = c.dados;
  if (!d) return null;
  return (
    <div class="form">
      {p.status === 'RASCUNHO' && <p class="aviso">A coleta ainda não começou: abra a pesquisa na aba Dados.</p>}
      <section class="bloco">
        <h3>Andamento geral: {pct(d.total.percentual)}</h3>
        <Barra pct={d.total.percentual} />
        <p class="dica">{inteiro(d.total.coletadas)} respostas · meta {inteiro(d.total.meta)}. Respostas acima da meta de um município não contam além dela.</p>
      </section>
      <section class="bloco">
        <h3>Por município (os mais atrasados primeiro)</h3>
        <div class="tabela-rolagem pequena">
          <table class="tabela">
            <thead><tr><th scope="col">Município</th><th scope="col" class="col-prog">Andamento</th><th scope="col" class="num">Coletadas</th><th scope="col" class="num">Meta</th></tr></thead>
            <tbody>
              {d.municipios.map((m) => (
                <tr key={m.codIbge}>
                  <td>{m.municipio}</td>
                  <td class="col-prog"><div class="prog-celula"><Barra pct={m.percentual} /><span>{pct(m.percentual)}</span></div></td>
                  <td class="num">{m.coletadas}</td>
                  <td class="num">{m.meta} {m.metaAtingida && <Selo texto="Meta atingida" tom="verde" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section class="bloco">
        <h3>Por pesquisador</h3>
        {d.pesquisadores.length === 0 ? <p class="vazio">Ninguém lançou resposta ainda.</p> : (
          <table class="tabela">
            <thead><tr><th scope="col">Pesquisador</th><th scope="col" class="num">Respostas</th><th scope="col">Última coleta</th></tr></thead>
            <tbody>{d.pesquisadores.map((x) => <tr key={x.pesquisadorId}><td>{x.nome}</td><td class="num">{x.respostas}</td><td>{dataHora(x.ultimaColeta)}</td></tr>)}</tbody>
          </table>
        )}
      </section>
    </div>
  );
}

// ------------------------------------------------------------------ respostas individuais (conferência e anulação)

type RespostaLinha = {
  id: string; codIbge: string; municipio: string; pesquisador: string; canal: string; faixaEtaria: string | null;
  sexo: string | null; notaAprovacao: number; coletadaEm: string; anuladaEm: string | null; anuladaMotivo: string | null;
};

function Respostas({ p, onMudou }: { p: PesquisaDetalhe; onMudou: () => Promise<void> }) {
  const [anuladas, setAnuladas] = useState(false);
  const [municipio, setMunicipio] = useState('');
  const [itens, setItens] = useState<RespostaLinha[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [anulando, setAnulando] = useState<RespostaLinha | null>(null);
  const { lista: municipios } = useMunicipios();

  async function carregar(depoisDe: string | null) {
    setCarregando(true);
    setErro('');
    try {
      const r = await api<{ itens: RespostaLinha[]; proximoCursor: string | null }>(`/pesquisas/${p.id}/respostas${qs({ anuladas: String(anuladas), municipio, cursor: depoisDe, limite: 25 })}`);
      setItens((a) => (depoisDe ? [...a, ...r.itens] : r.itens));
      setCursor(r.proximoCursor);
    } catch (e) {
      setErro(mensagemDoErro(e));
    } finally {
      setCarregando(false);
    }
  }
  useEffect(() => {
    void carregar(null);
  }, [anuladas, municipio]);

  return (
    <div class="form">
      <p class="dica">Confira o que foi lançado. Se uma resposta entrou por engano, anule-a: ela fica registrada com o motivo, mas sai dos resultados. O conteúdo das perguntas não é exibido aqui.</p>
      <div class="barra-filtros">
        <label class="campo"><span>Município</span>
          <select value={municipio} onChange={(e) => setMunicipio(e.currentTarget.value)}>
            <option value="">Todos</option>
            {municipios.map((m) => <option key={m.cod} value={m.cod}>{m.nome}</option>)}
          </select>
        </label>
        <label class="campo"><span>Mostrar</span>
          <select value={String(anuladas)} onChange={(e) => setAnuladas(e.currentTarget.value === 'true')}>
            <option value="false">Respostas válidas</option><option value="true">Respostas anuladas</option>
          </select>
        </label>
      </div>
      <ErroForm texto={erro} />
      <div class="tabela-rolagem pequena">
        <table class="tabela">
          <caption class="so-leitor">Respostas</caption>
          <thead><tr><th scope="col">Quando</th><th scope="col">Município</th><th scope="col">Pesquisador</th><th scope="col">Canal</th><th scope="col" class="num">Nota</th><th scope="col"><span class="so-leitor">Ação</span></th></tr></thead>
          <tbody>
            {itens.map((r) => (
              <tr key={r.id}>
                <td>{dataHora(r.coletadaEm)}</td>
                <td>{r.municipio}</td>
                <td>{r.pesquisador}</td>
                <td>{r.canal.toLowerCase()}{r.faixaEtaria ? ` · ${r.faixaEtaria}` : ''}{r.sexo && r.sexo !== 'NAO_INFORMADO' ? ` · ${r.sexo}` : ''}</td>
                <td class="num">{r.notaAprovacao}</td>
                <td>{r.anuladaEm ? <small title={r.anuladaMotivo ?? ''}>Anulada: {r.anuladaMotivo}</small> : <button type="button" class="link perigo-txt" onClick={() => setAnulando(r)}>Anular</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {carregando && itens.length === 0 && <Carregando />}
        {!carregando && itens.length === 0 && !erro && <p class="vazio">Nenhuma resposta {anuladas ? 'anulada' : 'lançada'}.</p>}
      </div>
      {cursor && <div class="modal-acoes"><button type="button" class="botao" disabled={carregando} onClick={() => void carregar(cursor)}>Carregar mais</button></div>}

      {anulando && <AnularResposta pesquisaId={p.id} r={anulando} onFechar={() => setAnulando(null)} onAnulada={async () => { setAnulando(null); await carregar(null); await onMudou(); }} />}
    </div>
  );
}

function AnularResposta({ pesquisaId: pid, r, onFechar, onAnulada }: { pesquisaId: string; r: RespostaLinha; onFechar: () => void; onAnulada: () => Promise<void> }) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  async function enviar(e: Event) {
    e.preventDefault();
    setSalvando(true);
    try {
      await api(`/pesquisas/${pid}/respostas/${r.id}/anular`, { method: 'POST', body: { motivo: motivo.trim() } });
      avisar('Resposta anulada.');
      await onAnulada();
    } catch (err) {
      setErro(mensagemDoErro(err));
      setSalvando(false);
    }
  }
  return (
    <Modal titulo="Anular resposta" onFechar={onFechar} pequeno>
      <form class="form" onSubmit={enviar}>
        <p>Resposta de <strong>{r.municipio}</strong>, nota {r.notaAprovacao}, lançada por {r.pesquisador} em {dataHora(r.coletadaEm)}.</p>
        <Campo rotulo="Motivo *" dica="Mínimo de 5 caracteres. Fica registrado.">{(i) => <textarea id={i} required minLength={5} maxLength={300} rows={3} value={motivo} onInput={(e) => setMotivo(e.currentTarget.value)} />}</Campo>
        <ErroForm texto={erro} />
        <div class="modal-acoes">
          <button type="button" class="botao" onClick={onFechar}>Cancelar</button>
          <button type="submit" class="botao perigo" disabled={salvando || motivo.trim().length < 5}>{salvando ? 'Anulando…' : 'Anular resposta'}</button>
        </div>
      </form>
    </Modal>
  );
}

// ------------------------------------------------------------------ resultado

function Resultado({ p, onFechar }: { p: PesquisaDetalhe; onFechar: () => void }) {
  const c = useApi<ResultadoPesquisa>(`/pesquisas/${p.id}/resultado`);
  if (c.carregando && !c.dados) return <Carregando />;
  if (c.erro) return <ErroForm texto={mensagemDoErro(c.erro)} />;
  const d = c.dados;
  if (!d) return null;
  const e = d.estado;
  const comResposta = d.municipios.filter((m) => m.n > 0).sort((a, b) => b.n - a.n);

  function verNoMapa() {
    pesquisaId.value = p.id;
    camada.value = 'satisfacao';
    onFechar();
    irPara('painel');
  }

  if (e.n === 0) return <p class="vazio-grande">Ainda não há respostas nesta pesquisa.</p>;

  return (
    <div class="form">
      <div class="kpis-modal">
        <div class="kpi kpi-grande"><span class="kpi-valor">{pct(e.aprovacao)}</span><span class="kpi-rotulo">aprovam{e.margemErro !== null ? ` (±${decimal(e.margemErro)} p.p.)` : ''}</span></div>
        <div class="kpi"><span class="kpi-valor">{inteiro(e.n)}</span><span class="kpi-rotulo">respostas</span></div>
        <div class="kpi"><span class="kpi-valor">{pct(e.reprovacao)}</span><span class="kpi-rotulo">reprovam</span></div>
        <div class="kpi"><span class="kpi-valor">{decimal(e.media)}</span><span class="kpi-rotulo">nota média</span></div>
        <div class="kpi"><span class="kpi-valor">{e.municipiosDisponiveis}/{e.municipiosComResposta}</span><span class="kpi-rotulo">municípios com resultado</span></div>
      </div>
      <p class="dica">
        Aprova = nota 7 a 10; reprova = 0 a 4. O total do estado soma todas as respostas sem ponderar pela população, e a margem de erro só vale para amostra aleatória
        {p.status === 'RASCUNHO' ? '' : ' — confira a metodologia'}. Município só tem resultado a partir de {inteiro(e.amostraMinima)} respostas.
        {e.aprovacaoMediaMunicipios !== null && ` Média simples dos municípios com resultado: ${pct(e.aprovacaoMediaMunicipios)}.`}
      </p>
      <div class="modal-acoes"><button type="button" class="botao" onClick={verNoMapa}>Ver no mapa</button></div>
      <div class="tabela-rolagem pequena">
        <table class="tabela">
          <caption class="so-leitor">Resultado por município</caption>
          <thead><tr><th scope="col">Município</th><th scope="col" class="num">Respostas</th><th scope="col" class="num">Meta</th><th scope="col">Aprovação</th></tr></thead>
          <tbody>
            {comResposta.map((m) => (
              <tr key={m.codIbge}>
                <td>{m.nome}</td>
                <td class="num">{m.n}</td>
                <td class="num">{m.meta ?? '—'}</td>
                <td>{m.disponivel ? <strong>{pct(m.aprovacao)} <small>±{decimal(m.margemErro)}</small></strong> : <Selo texto="Amostra insuficiente" tom="ambar" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
